import { Check } from 'lucide-react';
import type { WizardStep } from '@/lib/deployWizard';
import { cn } from '@/lib/utils';

export interface WizardStepperProps {
  steps: { id: WizardStep; label: string }[];
  current: WizardStep;
  /** Furthest step index reached; later steps are disabled. */
  maxStep: number;
  onGoTo: (step: WizardStep) => void;
  className?: string;
}

/** Use above the wizard body to show progress and jump back to steps already reached. */
export function WizardStepper({ steps, current, maxStep, onGoTo, className }: WizardStepperProps) {
  const currentIndex = steps.findIndex((s) => s.id === current);

  return (
    <nav aria-label="Deployment steps" className={className}>
      <ol className="flex items-center gap-2">
        {steps.map((step, i) => {
          const isCurrent = step.id === current;
          const done = i < currentIndex;
          const last = i === steps.length - 1;
          return (
            <li key={step.id} className={cn('flex min-w-0 items-center gap-2', !last && 'md:flex-1')}>
              <button
                type="button"
                aria-current={isCurrent ? 'step' : undefined}
                disabled={i > maxStep}
                onClick={() => onGoTo(step.id)}
                className="flex min-w-0 items-center gap-2 min-h-10 rounded-md px-1 py-2 text-sm font-medium outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-60"
              >
                <span
                  className={cn(
                    'flex size-7 shrink-0 items-center justify-center rounded-full border text-xs tabular-nums',
                    isCurrent
                      ? 'border-primary bg-primary text-primary-foreground'
                      : done
                        ? 'bg-accent text-accent-foreground'
                        : 'bg-background text-muted-foreground',
                  )}
                >
                  {done ? <Check className="size-4" aria-hidden /> : i + 1}
                </span>
                {/* Below md only the current label is shown; others stay available to screen readers. */}
                <span
                  className={cn(
                    'truncate',
                    isCurrent ? 'text-foreground' : 'sr-only md:not-sr-only',
                    !isCurrent && (done ? 'text-foreground' : 'text-muted-foreground'),
                  )}
                >
                  {step.label}
                  {done && <span className="sr-only"> (completed)</span>}
                </span>
              </button>
              {!last && <span aria-hidden className="hidden h-px flex-1 bg-border md:block" />}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
